import React from 'react';
import classNames from 'classnames';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'react-intl';
import { KeyboardReactInterface } from 'react-simple-keyboard';

import { useSelector } from 'hooks';

import { getLocation } from 'modules/router/selectors';
import { hasShareboxStyle } from 'modules/dealers/selectors';
import { getSelectedLanguage } from 'modules/translations/selectors';
import Language from 'types/LanguageEnum';
import { createDictionary } from 'utils/objectUtils';

import { Button } from 'components/ui';
import { ButtonType } from 'components/ui/Button';

import KeyboardInput from './KeyboardInput';
import ComposingState from './cache/composing';
import {
  getPortalContainer,
  KeyboardLayoutType,
  WRITABLE_INPUT_TYPES,
  getDefaultCandidates,
} from './utils/KeyUtils';
import JapaneseKeyboard from './Custom/Japanese';
import DefaultKeyboard from './Custom/Default';
import type { KeyboardComponentProps } from './Custom/types';

import './index.css';
import { SPECIAL_KEYS } from './utils/constants';

const KEYBOARDS = createDictionary<Language, React.FC<KeyboardComponentProps>>({
  [Language.JA_JP]: JapaneseKeyboard,
}, DefaultKeyboard);

// See https://stackoverflow.com/a/46012210
const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
const nativeTextAreaValueSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value').set;

const KeyboardModal = () => {
  const keyboardRef = React.useRef<KeyboardReactInterface>(null);
  const focusedInput = React.useRef<HTMLInputElement | null>(null);

  const location = useSelector(getLocation);
  const selectedLanguage = useSelector(getSelectedLanguage);

  const [value, setValue] = React.useState('');
  const [show, setShow] = React.useState(true);
  const [initialValue, setInitialValue] = React.useState('');
  const [selection, setSelection] = React.useState<[number, number]>([0, 0]);
  const type = focusedInput.current?.type;
  const isPassword = type === 'password';
  const maxLength = focusedInput.current?.maxLength;
  const isTextarea = focusedInput.current?.type === 'textarea';
  const layout = focusedInput.current?.dataset?.layout as KeyboardLayoutType;

  const candidates = React.useMemo(
    () => getDefaultCandidates(selectedLanguage),
    [selectedLanguage],
  );

  const isNumericInput = ['tel', 'number'].includes(type);
  const KeyboardComponent = isNumericInput ? DefaultKeyboard : KEYBOARDS[selectedLanguage];

  const onFocus = React.useCallback(({ target }: Event) => {
    focusedInput.current = target as HTMLInputElement;
    setValue(focusedInput.current.value);
    setInitialValue(focusedInput.current.value);
    setShow(true);
  }, []);

  const onClose = React.useCallback((text?: string) => {
    setShow(false);

    if (typeof text === 'string') {
      if (focusedInput.current instanceof HTMLTextAreaElement) {
        nativeTextAreaValueSetter.call(focusedInput.current, text);
      } else {
        nativeInputValueSetter.call(focusedInput.current, text);
      }

      const event = new Event('input', { bubbles: true });
      focusedInput.current.dispatchEvent(event);
    }

    document.body.removeChild(getPortalContainer());
  }, []);

  const handleChangeSelection = React.useCallback((newSelection: [number, number]) => {
    setSelection(newSelection);
  }, []);

  const handleKeyReleased = React.useCallback(
    (which: string) => {
      if (which === SPECIAL_KEYS.TAB) {
        onClose();
      }
    },
    [onClose],
  );

  const handleKeyboardInit = React.useCallback(
    (keyboard: KeyboardReactInterface) => {
      keyboard.setInput(value);
    },
    [value],
  );

  const handleCancel = React.useCallback(() => {
    onClose(initialValue);
  }, [onClose, initialValue]);

  const handleSend = React.useCallback(() => {
    onClose(value);
  }, [onClose, value]);

  const handleClear = React.useCallback(() => {
    setValue('');
    keyboardRef.current.clearInput();
  }, [setValue]);

  const handleChange = React.useCallback(async (newValue: string) => {
    if (maxLength > 0 && newValue.length > maxLength) {
      keyboardRef.current?.setInput(value); // resync keyboard internal state
      return;
    }
    setValue(newValue);
  }, [maxLength, value]);

  const setRef = React.useCallback((ref: KeyboardReactInterface) => {
    keyboardRef.current = ref;
  }, []);

  React.useLayoutEffect(() => {
    let inputs: NodeListOf<HTMLInputElement>;

    const observer = new MutationObserver(() => {
      inputs = document.querySelectorAll('input,textarea');
      inputs.forEach(input => {
        if (WRITABLE_INPUT_TYPES.includes(input.type) && input.id !== 'keyboard-input') {
          input.removeEventListener('focus', onFocus);
          input.addEventListener('focus', onFocus);
          input.removeEventListener('click', onFocus);
          input.addEventListener('click', onFocus);

          if (document.activeElement === input && focusedInput.current !== input) {
            onFocus({ target: input } as unknown as Event);
          }
        }
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      inputs?.forEach(input => {
        input.removeEventListener('focus', onFocus);
        input.removeEventListener('click', onFocus);
      });
      observer.disconnect();
    };
  }, [onFocus]);

  React.useEffect(() => {
    setShow(false);
  }, [location]);

  React.useEffect(
    () => () => {
      ComposingState.reset();
    },
    [],
  );

  return (
    show &&
    createPortal(
      <div className="z-60 fixed top-0 bottom-0 left-0 right-0">
        <div className="fixed h-screen w-screen bg-gray-900/25" onClick={handleSend} />
        <div className="fixed w-screen bottom-0">
          <div className="keyboard-inputWrapper mb-24 flex w-full m-auto max-w-6xl !min-w-96 justify-center px-12">
            <div className="bg-input-bg p-4 flex flex-col gap-4 w-full rounded-card">
              <KeyboardInput
                value={value}
                selection={selection ?? [0, 0]}
                onClear={handleClear}
                className={classNames({ 'whitespace-pre': !isTextarea })}
                isPassword={isPassword}
              />
              <div className="flex gap-4">
                <Button
                  onClick={handleCancel}
                  className="w-full flex justify-center items-center"
                  type={ButtonType.PRIMARY}
                >
                  <FormattedMessage id="cancel" defaultMessage="Cancel" />
                </Button>
                <Button onClick={handleSend} className="w-full flex justify-center items-center">
                  <FormattedMessage id="confirm.title" defaultMessage="Confirm" />
                </Button>
              </div>
            </div>
          </div>
          <div className="bg-input-bg p-4">
            <KeyboardComponent
              type={type}
              layoutType={layout}
              language={selectedLanguage}
              layoutCandidates={candidates}
              keyboardRef={setRef}
              onChange={handleChange}
              onInit={handleKeyboardInit}
              onKeyReleased={handleKeyReleased}
              onSelectionChange={handleChangeSelection}
            />
          </div>
        </div>
      </div>,
      getPortalContainer(),
    )
  );
};

// This is a safeguard to ensure that the keyboard is only displayed on Sharebox kiosks
const KeyboardModalWrapper = () => {
  const allowKeyboard = useSelector(hasShareboxStyle);
  return allowKeyboard && <KeyboardModal />;
};

export default KeyboardModalWrapper;
